import React, { useEffect } from 'react';
import {
  Box,
  Button,
  VStack,
  Input,
  Text,
  HStack,
  Textarea,
} from '@chakra-ui/react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Drawer, { DrawerHeader, DrawerTitle, DrawerBody, DrawerFooter, DrawerCloseTrigger } from '@/components/ui/Drawer';
import { FormErrorDisplay } from '@/components/ui/FormErrorDisplay';
import withHashDrawer from '@/components/ui/withHashDrawer';
import {
  useCreateConditionLookupMutation,
  useUpdateConditionLookupMutation,
  useGetConditionLookupQuery,
} from '../api';
import Select from '@/components/ui/Select';
import { useGetSpecialitiesQuery } from '@entities/specialities/api';
import { useFormErrorHandler } from '@hooks/useFormErrorHandler';
import { LOOKUP_STATUSES, LOOKUP_STATUS_OPTIONS } from '@/utils/lookupStatus';

// Schema definition
const conditionSchema = yup.object({
  name: yup
    .string()
    .required('Condition name is required')
    .min(2, 'Name must be at least 2 characters long')
    .max(100, 'Name must be less than 100 characters'),
  description: yup
    .string()
    .optional()
    .max(500, 'Description must be less than 500 characters'),
  status: yup
    .string()
    .required('Status is required')
    .oneOf(LOOKUP_STATUSES, 'Invalid status'),
  speciality_id: yup
    .number()
    .required('Speciality is required')
    .min(1, 'Please select a speciality')
});

type ConditionFormData = yup.InferType<typeof conditionSchema>;

interface ConditionCreateEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  entityId?: number; // ID of condition to fetch (for hash modal edit mode)
  onSuccess?: () => void;
}

const ConditionCreateEditModal: React.FC<ConditionCreateEditModalProps> = ({
  isOpen,
  onClose,
  entityId,
  onSuccess,
}) => {
  // Fetch individual entity if entityId is provided (hash modal edit mode)
  const { data: editingCondition } = useGetConditionLookupQuery(entityId!, {
    skip: !entityId,
  });

  const [createCondition, { isLoading: isCreating }] = useCreateConditionLookupMutation();
  const [updateCondition, { isLoading: isUpdating }] = useUpdateConditionLookupMutation();
  const { data: specialities = [] } = useGetSpecialitiesQuery();

  const isLoading = isCreating || isUpdating;
  const isEditMode = Boolean(entityId && editingCondition);

  const {
    control,
    handleSubmit,
    reset,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<ConditionFormData>({
    resolver: yupResolver(conditionSchema) as any,
    defaultValues: {
      name: '',
      description: '',
      status: 'draft',
      speciality_id: 0,
    },
  });

  const { withErrorHandling } = useFormErrorHandler(setError, clearErrors);

  // Handle modal close with form reset
  const handleClose = () => {
    reset({
      name: '',
      description: '',
      status: 'draft',
      speciality_id: 0,
    });
    onClose();
  };

  // Reset form when modal opens or editingCondition changes
  useEffect(() => {
    if (isOpen && editingCondition) {
      reset({
        name: editingCondition.name,
        description: editingCondition.description || '',
        status: editingCondition.status,
        speciality_id: editingCondition.speciality?.id || 0,
      });
    } else if (isOpen && !entityId) {
      // New mode - reset to empty values
      reset({
        name: '',
        description: '',
        status: 'draft',
        speciality_id: 0,
      });
    }
  }, [isOpen, editingCondition, entityId, reset]);

  const onSubmit = async (data: ConditionFormData) => {
    await withErrorHandling(async () => {
      if (isEditMode && editingCondition) {
        await updateCondition({ id: editingCondition.id, data }).unwrap();
      } else {
        await createCondition(data).unwrap();
      }

      handleClose();
      onSuccess?.();
    });
  };

  const specialityOptions = specialities.map(speciality => ({
    value: speciality.id.toString(),
    label: speciality.title,
  }));

  const statusOptions = LOOKUP_STATUS_OPTIONS;

  return (
    <Drawer isOpen={isOpen} onClose={handleClose} size="lg">
      <DrawerHeader>
        <DrawerTitle>{isEditMode ? 'Edit Condition' : 'Add New Condition'}</DrawerTitle>
        <DrawerCloseTrigger />
      </DrawerHeader>

      <form onSubmit={handleSubmit(onSubmit as any)} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <DrawerBody>
          <VStack gap={4} align="stretch">
            <Box>
              <Text mb={2} fontWeight="medium" fontSize="sm">
                Condition Name
              </Text>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    placeholder="Enter condition name"
                  />
                )}
              />
              <FormErrorDisplay error={errors.name} />
            </Box>

            <Box>
              <Text mb={2} fontWeight="medium" fontSize="sm">
                Description
              </Text>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <Textarea
                    {...field}
                    placeholder="Enter condition description (optional)"
                    rows={3}
                  />
                )}
              />
              <FormErrorDisplay error={errors.description} />
            </Box>

            <Box>
              <Text mb={2} fontWeight="medium" fontSize="sm">
                Speciality
              </Text>
              <Controller
                name="speciality_id"
                control={control}
                render={({ field }) => (
                  <Select
                    value={field.value?.toString() || ''}
                    onChange={(value) => field.onChange(parseInt(value))}
                    options={specialityOptions}
                    placeholder="Select speciality"
                  />
                )}
              />
              <FormErrorDisplay error={errors.speciality_id} />
            </Box>

            <Box>
              <Text mb={2} fontWeight="medium" fontSize="sm">
                Status
              </Text>
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onChange={(value) => field.onChange(value)}
                    options={statusOptions}
                  />
                )}
              />
              <FormErrorDisplay error={errors.status} />
            </Box>
          </VStack>
        </DrawerBody>

        <DrawerFooter>
          <HStack gap={3} width="full">
            <Button
              variant="ghost"
              onClick={handleClose}
              flex={1}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              colorPalette="brand"
              loading={isLoading}
              loadingText={isEditMode ? 'Updating...' : 'Creating...'}
              flex={1}
            >
              {isEditMode ? 'Update Condition' : 'Create Condition'}
            </Button>
          </HStack>
        </DrawerFooter>
      </form>
    </Drawer>
  );
};

// Export wrapped versions for create and edit modes
export const ConditionCreateModal = withHashDrawer(ConditionCreateEditModal, { 
  action: 'new'  // Create modal: /#/new
});

export const ConditionEditModal = withHashDrawer(ConditionCreateEditModal, { 
  action: 'edit' 
});

// Default export for backward compatibility - we'll use create mode
export default ConditionCreateModal; 
